import { useState, useEffect } from "react";
import { Eye, EyeOff, Check } from "lucide-react";
import { AuthContainer } from "./AuthContainer";
import { useAuth } from "./AuthContext";
import { useApp } from "./AppContext";
import { apiGet, apiPost } from "../services/api.service";
import { showError, showSuccess } from "../services/notification.service";

interface PasswordRequirement {
  text: string;
  met: boolean;
}

export function ResetPassword() {
  const { setCurrentStep } = useAuth();
  const { setIsAuthenticated } = useApp();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [requirements, setRequirements] = useState<PasswordRequirement[]>([
    { text: "Must be at least 8 characters", met: false },
    { text: "Must be at least 1 number", met: false },
    { text: "Must be at least 1 capital letter", met: false },
    { text: "Must be at least 1 special sign", met: false },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const [token, setToken] = useState("");

  useEffect(() => {
    // Get token from URL
    console.log("[RESET-PASSWORD] Current URL:", window.location.href);
    console.log("[RESET-PASSWORD] Search params:", window.location.search);
    
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get("token");
    
    console.log("[RESET-PASSWORD] Extracted token:", resetToken);
    
    if (resetToken) {
      setToken(resetToken);
      validateToken(resetToken);
    } else {
      // Don't show error immediately - the token might be coming
      console.log("[RESET-PASSWORD] No token found, waiting...");
      // Only show error after a short delay to allow for URL updates
      const timer = setTimeout(() => {
        if (!token) {
          showError("Invalid reset link");
          setCurrentStep(8); // Go to forgot password
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const newRequirements = [
      { text: "Must be at least 8 characters", met: password.length >= 8 },
      { text: "Must be at least 1 number", met: /\d/.test(password) },
      {
        text: "Must be at least 1 capital letter",
        met: /[A-Z]/.test(password),
      },
      {
        text: "Must be at least 1 special sign",
        met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      },
    ];
    setRequirements(newRequirements);
  }, [password]);

  const allRequirementsMet = requirements.every(req => req.met);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const validateToken = async (resetToken: string) => {
    setIsValidating(true);
    
    console.log("[RESET-PASSWORD] Validating token:", resetToken);
    console.log("[RESET-PASSWORD] Token length:", resetToken.length);

    try {
      const response = await apiGet("/auth/validate-reset-token", { token: resetToken }, {
        notification: { enabled: false }
      });
      
      console.log("[RESET-PASSWORD] Validation response:", response);
      
      if (response.success && response.data?.valid) {
        setTokenValid(true);
        setTokenEmail(response.data.email || "");
      } else {
        showError(response.data?.message || "Invalid or expired reset link");
        setCurrentStep(8); // Go to forgot password
      }
    } catch (error: any) {
      console.error("Token validation error:", error);
      showError("Failed to validate reset link");
      setCurrentStep(8); // Go to forgot password
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!allRequirementsMet || !passwordsMatch) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiPost("/auth/reset-password", {
        token,
        newPassword: password
      }, {
        notification: { enabled: false }
      });
      
      if (response.success) {
        showSuccess("Password reset successfully! Please sign in with your new password.");
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        // Go to sign in after a short delay
        setTimeout(() => {
          setCurrentStep(0); // Go to sign in
        }, 2000);
      } else {
        showError(response.data?.message || response.message || "Failed to reset password");
      }
    } catch (error: any) {
      console.error("Reset password error:", error);
      showError(error.message || "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <AuthContainer showBack={false} showProgress={false}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--ucm-primary-teal)] mx-auto"></div>
          <p className="mt-4 text-[var(--ucm-text-medium)]">Reset-Link wird überprüft...</p>
        </div>
      </AuthContainer>
    );
  }

  if (!tokenValid) {
    return null; // Will redirect to forgot password
  }

  return (
    <AuthContainer showBack={false} showProgress={false}>
      <div className="text-center mb-8">
        <h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-2 leading-tight">
          Reset Your Password
        </h1>
        {tokenEmail && (
          <p className="text-sm text-[var(--ucm-text-medium)]">
            Resetting password for: <strong>{tokenEmail}</strong>
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
            Neues Passwort
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full h-14 px-4 pr-12 border border-[var(--ucm-border-light)] rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
              placeholder="Neues Passwort eingeben"
              disabled={isLoading}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] hover:text-[var(--ucm-text-medium)]"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
            Neues Passwort bestätigen
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
                confirmPassword && !passwordsMatch
                  ? "border-[var(--ucm-error-red)]"
                  : "border-[var(--ucm-border-light)]"
              }`}
              placeholder="Neues Passwort bestätigen"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] hover:text-[var(--ucm-text-medium)]"
            >
              {showConfirmPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {confirmPassword && !passwordsMatch && (
            <p className="text-sm text-[var(--ucm-error-red)] mt-2">
              Passwörter stimmen nicht überein
            </p>
          )}
        </div>

        <div className="space-y-3">
          {requirements.map((requirement, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div
                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                  requirement.met
                    ? "bg-[var(--ucm-success-green)]"
                    : "bg-[var(--ucm-border-light)]"
                }`}
              >
                {requirement.met && <Check className="w-3 h-3 text-white" />}
              </div>
              <span
                className={`text-sm ${
                  requirement.met
                    ? "text-[var(--ucm-success-green)]"
                    : "text-[var(--ucm-text-light)]"
                }`}
              >
                {requirement.text}
              </span>
            </div>
          ))}
        </div>

        <button
          type="submit"
          disabled={!allRequirementsMet || !passwordsMatch || isLoading}
          className={`w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2 ${
            !allRequirementsMet || !passwordsMatch || isLoading
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[var(--ucm-primary-teal-hover)]"
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              Resetting...
            </div>
          ) : (
            "Reset Password"
          )}
        </button>
      </form>
    </AuthContainer>
  );
}